
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LegacyCity, LegacyJob } from '../types';
import { useCities } from '../src/contexts/CitiesContext';
import { getJobs } from '@/api';

interface FilterSectionProps {
  onSelectCity: (city: LegacyCity) => void;
  onSelectJob?: (job: LegacyJob) => void;
  onShowAllJobs: () => void;
  onShowAllCities: () => void;
  onShowRemoteJobs?: () => void;
  onShowHomeBasedJobs?: () => void;
  onSearchJobsWithQuery?: (query: string) => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  onSelectCity,
  onSelectJob,
  onShowAllJobs,
  onShowAllCities,
  onShowRemoteJobs,
  onShowHomeBasedJobs,
  onSearchJobsWithQuery
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [searchTerm, setSearchTerm] = useState('');
  const { cities: allCities, cityJobCounts, citiesMap } = useCities();

  // 从 API 加载职位数据（用于搜索）
  const [apiJobs, setApiJobs] = useState<LegacyJob[]>([]);
  useEffect(() => {
    getJobs().then(data => setApiJobs(data as LegacyJob[])).catch(() => {});
  }, []);

  // 获取城市名称（根据语言，兼容新旧格式）
  const getCityName = (city: { name?: string; enName?: string; nameZh?: string; nameEn?: string }) => {
    const zh = city.nameZh || city.name || '';
    const en = city.nameEn || city.enName || '';
    return currentLang.startsWith('zh') ? zh : en;
  };

  // 获取城市副标题（显示另一种语言作为辅助）
  const getCitySubtitle = (city: { name?: string; enName?: string; nameZh?: string; nameEn?: string }) => {
    const zh = city.nameZh || city.name || '';
    const en = city.nameEn || city.enName || '';
    return currentLang.startsWith('zh') ? en : zh;
  };

  // 翻译工作类型
  const getJobTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      'FULL_TIME': 'fullTime',
      'PART_TIME': 'partTime',
      'INTERNSHIP_ONLINE': 'internshipOnline',
      'INTERNSHIP_OFFLINE': 'internshipOffline'
    };
    const key = typeMap[type] || 'fullTime';
    return t(`jobData.jobTypes.${key}`, type);
  };

  // 翻译部门
  const getDepartmentLabel = (dept: string) => {
    const deptMap: Record<string, string> = {
      'Engineering': 'engineering',
      'Product': 'product',
      'Marketing': 'marketing',
      'Sales': 'sales',
      'Operations': 'operations',
      'Business Development': 'businessDevelopment',
      'Data': 'data',
      'Finance': 'finance',
      'Campus Ambassador': 'campusAmbassador'
    };
    const key = deptMap[dept] || dept.toLowerCase().replace(/\s+/g, '');
    return t(`jobData.departments.${key}`, dept);
  };

  // 格式化位置信息（翻译城市和国家）
  const formatJobLocation = (location: string) => {
    // Handle special global remote location
    if (location === 'Remote (Global)') {
      return t('jobData.locations.remoteGlobal', location);
    }

    // Handle "City/Remote" format
    if (location.includes('/Remote')) {
      const cityPart = location.replace('/Remote', '');
      const cityData = [...citiesMap.values()].find(c => cityPart === c.nameZh || cityPart === c.nameEn);
      const cityName = cityData ? getCityName(cityData) : cityPart;
      const remoteText = t('jobData.locations.remote', 'Remote');
      return `${cityName}/${remoteText}`;
    }

    // Split country and city parts
    const parts = location.split(', ');
    
    // Country translation mapping
    const countryMap: Record<string, string> = {
      'China': 'china',
      'UK': 'uk',
      'USA': 'usa',
      'India': 'india',
      'Vietnam': 'vietnam',
      'Malaysia': 'malaysia',
      'Singapore': 'singapore',
      'Thailand': 'thailand',
      'Indonesia': 'indonesia',
      'South Korea': 'southKorea'
    };

    let countryName = '';
    if (parts.length >= 2) {
      const countryKey = countryMap[parts[parts.length - 1]];
      countryName = countryKey ? t(`jobData.countries.${countryKey}`, parts[parts.length - 1]) : parts[parts.length - 1];
    }

    // Handle city part
    const cityPart = parts[0] || location;
    const cityNames = cityPart.split('/').map(city => {
      const trimmedCity = city.trim();
      const cityData = [...citiesMap.values()].find(c => trimmedCity === c.nameZh || trimmedCity === c.nameEn);
      return cityData ? getCityName(cityData) : trimmedCity;
    }).join('/');

    if (parts.length >= 2) {
      return `${cityNames}, ${countryName}`;
    }

    return cityNames;
  };

  // Filter and sort hot cities (actual job count > 0)
  // Priority: cities with offices > home-based cities; same type sorted by job count desc
  const hotCities = useMemo(() => {
    return allCities
      .filter(city => (cityJobCounts[city.id] || 0) > 0)
      .sort((a, b) => {
        // Cities with offices come first (isRemoteOnly: true or undefined goes last)
        const aIsRemoteOnly = a.isRemoteOnly === true;
        const bIsRemoteOnly = b.isRemoteOnly === true;

        if (aIsRemoteOnly !== bIsRemoteOnly) {
          return aIsRemoteOnly ? 1 : -1;
        }

        // Same type sorted by job count desc
        return (cityJobCounts[b.id] || 0) - (cityJobCounts[a.id] || 0);
      });
  }, [allCities, cityJobCounts]);

  // 搜索过滤逻辑 - 同时搜索城市和岗位
  const filteredCities = useMemo(() => {
    if (!searchTerm.trim()) return allCities;
    return allCities.filter(city =>
      (city.nameZh || city.name || '').includes(searchTerm) ||
      (city.nameEn || city.enName || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, allCities]);

  const filteredJobs = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase();
    return apiJobs.filter(job =>
      job.title.toLowerCase().includes(term) ||
      job.department.toLowerCase().includes(term) ||
      job.location.toLowerCase().includes(term)
    ).slice(0, 5); // Only show top 5 results
  }, [searchTerm, apiJobs]);

  // 判断是否有搜索结果
  const hasSearchResults = searchTerm.trim() && (filteredCities.length > 0 || filteredJobs.length > 0);

  return (
    <section className="relative max-w-7xl mx-auto px-4 sm:px-6 z-10 pb-8 sm:pb-12">
      <div className="bg-white rounded-xl shadow-2xl shadow-slate-200/50 p-4 sm:p-6 md:p-8 lg:p-12">

        {/* 1. 搜索框 - 新增 */}
        <div className="mb-8 sm:mb-12">
          <h2 className="text-xs sm:text-sm font-bold uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {t('filter.searchTitle')}
          </h2>
          <div className="relative">
            <input
              type="text"
              placeholder={t('filter.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-4 pl-11 sm:pl-10 sm:py-3.5 text-base sm:text-base font-semibold focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none cursor-text min-h-[52px] sm:min-h-[48px]"
            />
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 sm:w-4 sm:h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* 搜索结果 */}
          {hasSearchResults && (
            <div className="mt-4 space-y-3">
              {/* 岗位搜索结果 */}
              {filteredJobs.length > 0 && (
                <div>
                  <h3 className="text-[10px] font-bold uppercase text-slate-400 tracking-widest mb-2 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {t('filter.jobsLabel')} ({filteredJobs.length})
                  </h3>
                  <div className="space-y-2">
                    {filteredJobs.map(job => (
                      <div
                        key={job.id}
                        onClick={() => onSelectJob && onSelectJob(job)}
                        className="bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-xl p-3.5 sm:p-4 cursor-pointer transition-all group active:bg-blue-100 active:scale-[0.99] min-h-[60px] sm:min-h-0"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-sm font-bold text-slate-900 truncate group-hover:text-blue-600 transition-colors">
                                {job.title}
                              </h4>
                              <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 border border-blue-200">
                                {getDepartmentLabel(job.department)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span>{formatJobLocation(job.location)}</span>
                              <span>•</span>
                              <span>{getJobTypeLabel(job.type)}</span>
                            </div>
                          </div>
                          <div className="text-blue-600 group-hover:text-blue-700 flex items-center justify-center min-w-[44px] min-h-[44px]">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 城市搜索结果 */}
              {filteredCities.length > 0 && (
                <div>
                  <h3 className="text-[10px] font-bold uppercase text-slate-400 tracking-widest mb-2 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {t('filter.citiesLabel')} ({filteredCities.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
                    {filteredCities.map((city) => (
                      <div
                        key={city.id}
                        onClick={() => onSelectCity(city)}
                        className="group relative aspect-[4/3] sm:aspect-auto sm:h-24 rounded-xl overflow-hidden cursor-pointer shadow-sm hover:shadow-lg active:shadow-md active:scale-[0.97] transition-all"
                      >
                        <img
                          src={city.image}
                          alt={city.name}
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 active:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/50 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-2.5 sm:p-2 text-white">
                          <div className="text-[11px] sm:text-[9px] font-medium text-blue-300 mb-0.5 sm:mb-1 truncate">{getCitySubtitle(city)}</div>
                          <div className="flex items-center justify-between gap-1.5">
                            <div className="text-base sm:text-xs font-bold truncate flex-1">{getCityName(city)}</div>
                            <div className="text-xs sm:text-[9px] bg-white/20 backdrop-blur-sm px-1.5 sm:px-1 py-0.5 sm:py-0.5 rounded-lg shrink-0 font-semibold">
                              {cityJobCounts[city.id]}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 2. 城市展示 - 桌面端跑马灯，移动端网格 */}
        <div className="mb-8 sm:mb-12" style={{ display: hasSearchResults ? 'none' : 'block' }}>
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-xs sm:text-sm font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              {t('filter.hotCitiesTitle')} ({hotCities.length})
            </h2>
            <button
              onClick={onShowAllCities}
              className="text-xs sm:text-sm font-semibold text-blue-600 hover:text-blue-700 cursor-pointer transition-colors flex items-center gap-1"
            >
              {t('filter.viewAllCities')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* 桌面端：跑马灯 */}
          <div className="hidden md:block overflow-hidden whitespace-nowrap">
            <div
              className="inline-flex gap-4 animate-marquee hover:[animation-play-state:paused]"
              style={{ width: 'fit-content' }}
            >
              {[...hotCities, ...hotCities].map((city, index) => (
                <div
                  key={`${city.id}-${index}`}
                  onClick={() => onSelectCity(city)}
                  className="group relative h-32 w-48 shrink-0 rounded-xl overflow-hidden cursor-pointer
                             shadow-sm hover:shadow-xl active:scale-[0.98] transition-all"
                >
                  <img
                    src={city.image}
                    alt={city.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2 text-white">
                    <div className="text-[10px] font-medium text-blue-300 mb-1 truncate">
                      {getCitySubtitle(city)}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-bold truncate">{getCityName(city)}</div>
                      <div className="text-[9px] bg-white/20 backdrop-blur-sm px-1.5 py-0.5 rounded">
                        {cityJobCounts[city.id]}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 移动端：网格布局，只显示前6个 */}
          <div className="md:hidden grid grid-cols-2 gap-2.5">
            {hotCities.slice(0, 6).map((city) => (
              <div
                key={city.id}
                onClick={() => onSelectCity(city)}
                className="group relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer
                           shadow-sm hover:shadow-lg active:shadow-md active:scale-[0.97] transition-all"
              >
                <img
                  src={city.image}
                  alt={city.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 active:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/50 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                  <div className="text-[11px] font-medium text-blue-300 mb-1 truncate">{getCitySubtitle(city)}</div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-base font-bold truncate flex-1">{getCityName(city)}</div>
                    <div className="text-xs bg-white/20 backdrop-blur-sm px-2 py-1 rounded-lg shrink-0 font-semibold">
                      {cityJobCounts[city.id]}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* 移动端：查看全部城市按钮 */}
          {hotCities.length > 6 && (
            <div className="md:hidden mt-3">
              <button
                onClick={onShowAllCities}
                className="w-full py-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 active:bg-slate-200 text-sm font-semibold text-blue-600 transition-colors cursor-pointer flex items-center justify-center gap-1.5 min-h-[48px]"
              >
                {t('filter.viewAllCities')} ({hotCities.length})
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {!hasSearchResults && searchTerm && (
            <div className="text-center py-12 text-slate-500">
              <svg className="w-12 h-12 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="font-semibold">{t('filter.noResults')}</p>
            </div>
          )}
        </div>

        {/* 3. Remote Internship Banner - 移动端优化 */}
        <div className="relative overflow-hidden bg-slate-900 rounded-xl p-4 sm:p-6 md:p-8 mb-3 sm:mb-4">
          <div className="absolute top-0 right-0 -translate-y-1/3 translate-x-1/3 w-40 h-40 sm:w-56 sm:h-56 bg-blue-500/20 rounded-full blur-3xl"></div>
          <div className="relative z-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-500/20 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">{t('filter.remoteTitle')}</h3>
                  <p className="text-blue-400 text-xs font-semibold">25+ {t('filter.remoteOpenings')}</p>
                </div>
              </div>
              <p className="text-slate-400 text-xs sm:text-sm line-clamp-2 sm:line-clamp-none">
                {t('filter.remoteDescription')}
              </p>
            </div>
            <button
              onClick={onShowRemoteJobs || onShowAllJobs}
              className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg shadow-blue-500/20 text-sm min-h-[48px] flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>{t('filter.remoteBtn')}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* 4. Home-based Jobs Banner - 居家办公入口，移动端优化 */}
        <div className="relative overflow-hidden bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl p-4 sm:p-6 md:p-8">
          <div className="absolute top-0 left-0 -translate-y-1/3 -translate-x-1/3 w-40 h-40 sm:w-56 sm:h-56 bg-white/10 rounded-full blur-3xl"></div>
          <div className="relative z-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">{t('filter.homeBasedTitle')}</h3>
                  <p className="text-white/80 text-xs font-semibold">{t('filter.homeBasedWorkAnywhere')}</p>
                </div>
              </div>
              <p className="text-white/80 text-xs sm:text-sm line-clamp-2 sm:line-clamp-none">
                {t('filter.homeBasedDescription')}
              </p>
            </div>
            <button
              onClick={onShowHomeBasedJobs || onShowAllJobs}
              className="w-full sm:w-auto bg-white hover:bg-green-50 active:bg-green-100 text-green-600 px-6 py-3 rounded-xl font-bold transition-all shadow-lg text-sm min-h-[48px] flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>{t('filter.homeBasedBtn')}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

      </div>
    </section>
  );
};

export default FilterSection;

